import UserIcon from '@/assets/svg/header/user.svg';
import CloseIcon from '@/assets/svg/ui-elements/close.svg';
import LogOutIcon from '@/assets/svg/ui-elements/logout-red.svg';
import PlusIcon from '@/assets/svg/ui-elements/plus-green.svg';
import { colors } from '@/design-system/colors';
import { Permission, useCan } from '@/shared/modules/permissions';

import Tooltip from '../Tooltip';
import Typography from '../Typography';
import { PENDING_INVITATION_NOTE, PENDING_INVITATION_TOOLTIP, ROLE_CONFIG } from './data';
import { EntityItem } from './entity-item';
import { RoleItem } from './role-item';
import { useUserAccount } from './use-user-account';
import {
  AddEntityButton,
  DropdownCloseButton,
  DropdownCloseRow,
  DropdownDivider,
  DropdownSection,
  EntitiesScrollContainer,
  LogoutButton,
  ManageAccountLink,
  SectionLabel,
  TooltipRow,
  UserHeader,
} from './UserDropdown.styled';

type Props = {
  data: ReturnType<typeof useUserAccount>;
  onClose?: () => void;
};

export const UserAccountContent = ({ data, onClose }: Props) => {
  const {
    user,
    entities,
    logout,
    selectedEntity,
    isCreateEntityLocked,
    createEntityTooltipTitle,
    isEntityAccessLocked,
    entityAccessTooltipTitle,
    handleRoleClick,
    handleSelectEntity,
    handleCreateEntity,
    selectedUserRole,
    setOpen,
    ownProfileHref,
    pendingGammaInvitation,
    pendingGammaNote,
    pendingGammaTooltip,
    pendingEntityInvitations,
  } = data;

  // Mirrors `SessionMintingAuthorityService.mayMintPlatformWideSession`: the API refuses
  // to mint a Gamma Prime session without this permission, so the switcher must not offer one.
  const canSwitchToGammaSession = useCan(Permission.GlobalAdminCrossEntityAccess);

  return (
    <>
      {onClose && (
        <DropdownCloseRow>
          <DropdownCloseButton onClick={onClose} aria-label="Close">
            <CloseIcon width={20} height={20} />
          </DropdownCloseButton>
        </DropdownCloseRow>
      )}

      <UserHeader>
        {user?.name && (
          <Typography variant="body2" fontWeight={500} noWrap>
            {user.name}
          </Typography>
        )}
        <Tooltip title={user?.email} side="top">
          <Typography variant="body3" color={colors.text.secondary} noWrap>
            {user?.email}
          </Typography>
        </Tooltip>
      </UserHeader>

      <DropdownDivider />

      {ownProfileHref && (
        <>
          <ManageAccountLink href={ownProfileHref} onClick={() => setOpen(false)}>
            <UserIcon />
            <Typography variant="body2">My Profile</Typography>
          </ManageAccountLink>

          <DropdownDivider />
        </>
      )}

      <DropdownSection>
        <RoleItem
          name={ROLE_CONFIG.user.label}
          isActive={selectedUserRole === 'user'}
          dotColor={ROLE_CONFIG.user.dot}
          onSelect={() => handleRoleClick('user')}
        />

        {canSwitchToGammaSession && (
          <RoleItem
            name={ROLE_CONFIG.admin.label}
            isActive={selectedUserRole === 'admin'}
            dotColor={ROLE_CONFIG.admin.dot}
            onSelect={() => handleRoleClick('admin')}
          />
        )}

        {!canSwitchToGammaSession && pendingGammaInvitation && (
          <RoleItem
            name={ROLE_CONFIG.admin.label}
            isActive={false}
            dotColor={ROLE_CONFIG.admin.dot}
            disabled
            note={pendingGammaNote}
            tooltipTitle={pendingGammaTooltip}
            onSelect={() => {}}
          />
        )}
      </DropdownSection>

      <DropdownDivider />

      <DropdownSection>
        <SectionLabel>
          <Typography variant="body3" color={colors.text.secondary}>
            Your Entities:
          </Typography>
        </SectionLabel>

        <EntitiesScrollContainer>
          {entities?.map((el) => (
            <EntityItem
              key={el.entityId}
              entity={el}
              isActive={selectedEntity?.entityId === el.entityId}
              isLocked={isEntityAccessLocked}
              tooltipTitle={entityAccessTooltipTitle}
              onSelect={handleSelectEntity}
            />
          ))}

          {pendingEntityInvitations.map((affiliation) => (
            <RoleItem
              key={`pending-${affiliation.entityId}`}
              name={affiliation.entityDisplayName}
              isActive={false}
              dotColor={ROLE_CONFIG.entity.dot}
              disabled
              note={PENDING_INVITATION_NOTE}
              tooltipTitle={PENDING_INVITATION_TOOLTIP}
              onSelect={() => {}}
            />
          ))}
        </EntitiesScrollContainer>

        <Tooltip title={createEntityTooltipTitle} side="top" disabled={!isCreateEntityLocked}>
          <TooltipRow>
            <AddEntityButton variant="ghost" color="primary" fullWidth onClick={handleCreateEntity}>
              <PlusIcon />
              Add New Legal Entity
            </AddEntityButton>
          </TooltipRow>
        </Tooltip>
      </DropdownSection>

      <DropdownDivider />

      <LogoutButton
        onClick={() => {
          setOpen(false);
          logout();
        }}
      >
        <LogOutIcon />
        <Typography variant="body2" color="inherit">
          Log Out
        </Typography>
      </LogoutButton>
    </>
  );
};
